import { useState, useEffect } from 'react';

interface EstimateModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function EstimateModal({ isOpen, onClose }: EstimateModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    const form = e.currentTarget;
    const formData = new FormData(form);

    // Convert to URL-encoded format
    const urlEncodedData = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === 'string') {
        urlEncodedData.append(key, value);
      }
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d6b7kstbd17lru29m92g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: urlEncodedData.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        form.reset();
        setTimeout(() => {
          onClose();
          setSubmitStatus('idle');
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors cursor-pointer z-10"
        >
          <i className="ri-close-line text-2xl text-gray-600"></i>
        </button>

        {/* Header */}
        <div className="bg-gradient-to-r from-gray-900 to-gray-800 p-8 text-white">
          <h2 className="text-3xl font-bold mb-2">Request Free Estimate</h2>
          <p className="text-gray-300">Tell us about your project and we'll get back to you within 24 hours</p>
        </div>

        {/* Form */}
        <form id="estimate-form" onSubmit={handleSubmit} data-readdy-form className="p-8">
          <div className="space-y-6">
            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition-all"
                placeholder="John Smith"
              />
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition-all"
                placeholder="john@example.com"
              />
            </div>

            {/* Phone */}
            <div>
              <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                required
                className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition-all"
                placeholder="(123) 456-7890"
              />
            </div>

            {/* Service Type */}
            <div>
              <label htmlFor="service" className="block text-sm font-semibold text-gray-700 mb-2">
                Service Needed <span className="text-red-500">*</span>
              </label>
              <select
                id="service"
                name="service"
                required
                className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition-all cursor-pointer"
              >
                <option value="">Select a service</option>
                <option value="Emergency Mobile Repair">Emergency Mobile Repair</option>
                <option value="Custom Fabrication">Custom Fabrication</option>
                <option value="Commercial & Industrial">Commercial & Industrial</option>
                <option value="Pipe Welding">Pipe Welding</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* Project Details */}
            <div>
              <label htmlFor="details" className="block text-sm font-semibold text-gray-700 mb-2">
                Project Details <span className="text-red-500">*</span>
              </label>
              <textarea
                id="details"
                name="details"
                required
                maxLength={500}
                rows={4}
                className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition-all resize-none"
                placeholder="Please describe your welding or fabrication needs, including materials, dimensions, and timeline..."
              ></textarea>
              <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
            </div>

            {/* Location */}
            <div>
              <label htmlFor="location" className="block text-sm font-semibold text-gray-700 mb-2">
                Project Location
              </label>
              <input
                type="text"
                id="location"
                name="location"
                className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition-all"
                placeholder="City, State or Full Address"
              />
            </div>

            {/* Urgency */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Project Urgency
              </label>
              <div className="flex flex-wrap gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="urgency"
                    value="Emergency (ASAP)"
                    className="w-4 h-4 text-orange-500 cursor-pointer"
                  />
                  <span className="text-sm text-gray-700">Emergency (ASAP)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="urgency"
                    value="Within a week"
                    className="w-4 h-4 text-orange-500 cursor-pointer"
                  />
                  <span className="text-sm text-gray-700">Within a week</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="urgency"
                    value="Flexible timeline"
                    defaultChecked
                    className="w-4 h-4 text-orange-500 cursor-pointer"
                  />
                  <span className="text-sm text-gray-700">Flexible timeline</span>
                </label>
              </div>
            </div>
          </div>

          {/* Submit Status */}
          {submitStatus === 'success' && (
            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-check-line text-xl text-green-600"></i>
              </div>
              <p className="text-sm text-green-700 font-semibold">
                Request submitted successfully! We'll contact you soon.
              </p>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-error-warning-line text-xl text-red-600"></i>
              </div>
              <p className="text-sm text-red-700 font-semibold">
                Something went wrong. Please try again or call us directly.
              </p>
            </div>
          )}

          {/* Submit Button */}
          <div className="mt-8 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="whitespace-nowrap flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold text-base px-6 py-4 rounded-lg transition-all duration-300 cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="whitespace-nowrap flex-1 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 text-white font-bold text-base px-6 py-4 rounded-lg transition-all duration-300 cursor-pointer disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Request'}
            </button>
          </div>

          <p className="text-xs text-gray-500 text-center mt-4">
            By submitting this form, you agree to be contacted about your project
          </p>
        </form>
      </div>
    </div>
  );
}
